if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.initRequiredOptionValidation();
        this.applyGenderActiveFromUrl();
        if (this.requiresManualVariantSelection()) {
          this.updateManualVariantState();
        }
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      applyGenderActiveFromUrl() {
        const genderParam = new URLSearchParams(window.location.search).get('gender');
        if (!genderParam) return;

        const normalizedGender = genderParam.toLowerCase().trim();
        let genderKey = normalizedGender;
        if (normalizedGender === 'mens' || normalizedGender === 'male') genderKey = 'men';
        if (normalizedGender === 'womens' || normalizedGender === 'female' || normalizedGender === 'woman') {
          genderKey = 'women';
        }
        if (genderKey !== 'men' && genderKey !== 'women') return;

        const genderLabel = genderKey === 'women' ? 'Women' : 'Men';
        const genderContainer = this.querySelector('.gender_button_row');
        if (!genderContainer) return;

        // Linked gender products (<a data-gender-param>) and any other param-based controls
        genderContainer.querySelectorAll('.variant-button[data-gender-param]').forEach((el) => {
          el.classList.toggle('g-active', el.dataset.genderParam === genderKey);
        });

        // Unisex products (buttons use data-gender-label)
        const unisexButtons = genderContainer.querySelectorAll('.js-unisex-gender-toggle');
        unisexButtons.forEach((btn) => {
          const label = (btn.dataset.genderLabel || btn.textContent || '').toLowerCase().trim();
          const isActive =
            label === genderKey ||
            (genderKey === 'men' && label === 'man') ||
            (genderKey === 'women' && (label === 'woman' || label === 'womens'));
          btn.classList.toggle('g-active', isActive);
          btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });

        if (unisexButtons.length) {
          const applyUnisexFromUrl = () => {
            const unisexFilter = this.querySelector('unisex-size-filter');
            if (unisexFilter?.applyFilter) {
              unisexFilter.applyFilter(genderLabel);
            }
            applyWomenOnlySwatchVisibility(this);
          };
          // Filter may connect after product-info; apply now and on next frame
          applyUnisexFromUrl();
          requestAnimationFrame(applyUnisexFromUrl);
        }

        this.refreshRequiredOptionErrors();
        if (this.requiresManualVariantSelection?.()) {
          this.updateManualVariantState?.();
        }
      }

      requiresManualVariantSelection() {
        return this.variantSelectors?.dataset.requiresManualVariantSelection === 'true';
      }

      isPdpContext() {
        if (this.closest('quick-add-modal')) return false;
        return this.dataset.updateUrl === 'true' || this.id?.startsWith('MainProduct');
      }

      setAwaitingSelectionButton() {
        const hasAvailableVariant = (this.getProductData()?.variants || []).some((variant) => variant.available);
        if (!hasAvailableVariant) {
          this.toggleSubmitButton(true, window.variantStrings.soldOut);
          return;
        }

        // PDP: show which options are still missing on the button
        // Collection / quick-add: keep disabled look, but leave ATC label unchanged
        if (this.isPdpContext()) {
          this.toggleSubmitButton(true, this.getAwaitingSelectionButtonLabel(), true);
        } else {
          this.toggleSubmitButton(true, window.variantStrings?.addToCart, true);
        }
      }

      hasRequiredOption(optionKey) {
        if (!this.variantSelectors) return false;

        const matchesKey = (name) => {
          const normalized = (name || '').toLowerCase();
          return optionKey === 'size' ? normalized.includes('size') : normalized.includes('width');
        };

        const hasNamedInput = Array.from(this.variantSelectors.querySelectorAll('[data-option-name]')).some((el) =>
          matchesKey(el.dataset.optionName)
        );
        if (hasNamedInput) return true;

        return !!this.variantSelectors.querySelector(`[data-required-option="${optionKey}"]`);
      }

      getRequiredOptionGroups() {
        const groups = { width: false, size: false };
        if (!this.variantSelectors) return groups;

        this.variantSelectors.querySelectorAll('input[type="radio"]:checked[data-option-name]').forEach((input) => {
          const name = (input.dataset.optionName || '').toLowerCase();
          if (name.includes('size')) groups.size = true;
          if (name.includes('width')) groups.width = true;
        });

        this.variantSelectors.querySelectorAll('select.select__select').forEach((select) => {
          if (!select.value) return;
          const selectedOption = select.selectedOptions[0];
          const name = (selectedOption?.dataset?.optionName || '').toLowerCase();
          if (name.includes('size')) {
            groups.size = true;
          } else if (name.includes('width')) {
            groups.width = true;
          } else {
            const container = select.closest('[data-required-option]');
            const key = container?.dataset.requiredOption;
            if (key === 'size') groups.size = true;
            if (key === 'width') groups.width = true;
          }
        });

        return groups;
      }

      areRequiredOptionsSelected() {
        if (!this.isGenderSelected()) return false;
        if (!this.requiresManualVariantSelection()) return true;

        const groups = this.getRequiredOptionGroups();
        return (
          (!this.hasRequiredOption('width') || groups.width) &&
          (!this.hasRequiredOption('size') || groups.size)
        );
      }

      isGenderSelected() {
        if (!this.variantSelectors) return true;

        const genderContainer = this.variantSelectors.querySelector('[data-required-option="gender"]');
        if (!genderContainer) return true;

        // Gender UI is hidden in collection Shop More / quick-add — don't block ATC there
        if (this.closest('quick-add-modal')) return true;

        if (genderContainer.querySelector('.js-unisex-gender-toggle')) {
          return !!genderContainer.querySelector('.js-unisex-gender-toggle.g-active');
        }

        return !!genderContainer.querySelector('.variant-button[data-gender-param].g-active');
      }

      getMissingRequiredOptions() {
        const missing = [];

        if (
          !this.closest('quick-add-modal') &&
          !this.isGenderSelected() &&
          this.variantSelectors?.querySelector('[data-required-option="gender"]')
        ) {
          missing.push('gender');
        }

        if (this.requiresManualVariantSelection()) {
          const groups = this.getRequiredOptionGroups();
          if (this.hasRequiredOption('size') && !groups.size) missing.push('size');
          if (this.hasRequiredOption('width') && !groups.width) missing.push('width');
        }

        return missing;
      }

      getAwaitingSelectionButtonLabel() {
        const missing = this.getMissingRequiredOptions();
        if (!missing.length) return window.variantStrings?.addToCart || 'Add to cart';

        const labels = missing.map((key) => {
          const container = this.variantSelectors?.querySelector(`[data-required-option="${key}"]`);
          let title =
            container?.querySelector('[data-option-name]')?.dataset?.optionName ||
            container?.querySelector('legend, .form__label')?.textContent ||
            '';

          if (key === 'gender') {
            title =
              this.querySelector('.gender_title, h2.select_gender')?.textContent || title;
          }

          title = (title || key).replace(/\s+/g, ' ').trim().replace(/^select\s+/i, '');
          return title.replace(/\b\w/g, (char) => char.toUpperCase());
        });

        if (labels.length === 1) return `Select ${labels[0]}`;
        if (labels.length === 2) return `Select ${labels[0]} & ${labels[1]}`;
        return `Select ${labels.slice(0, -1).join(', ')} & ${labels[labels.length - 1]}`;
      }

      isRequiredOptionInView(container) {
        if (!container) return true;

        const scrollTarget =
          container.querySelector('.select_gender, .form__label, legend') || container;
        const headerOffset = 120;
        const rect = scrollTarget.getBoundingClientRect();
        const bottomPadding = 40;

        return rect.top >= headerOffset && rect.bottom <= window.innerHeight - bottomPadding;
      }

      scrollToRequiredOption(optionKey) {
        const container = this.variantSelectors?.querySelector(`[data-required-option="${optionKey}"]`);
        if (!container || this.isRequiredOptionInView(container)) return;

        const scrollTarget =
          container.querySelector('.select_gender, .form__label, legend') || container;
        const headerOffset = 120;
        const stickyInfo = this.querySelector('.product__column-sticky');
        const useStickyScroll =
          stickyInfo && window.matchMedia('(min-width: 750px)').matches;

        if (useStickyScroll) {
          const targetRect = scrollTarget.getBoundingClientRect();
          const stickyTop = Math.max(stickyInfo.getBoundingClientRect().top, headerOffset);
          const scrollTop = window.scrollY + targetRect.top - stickyTop;
          window.scrollTo({ top: Math.max(scrollTop, 0), behavior: 'smooth' });
          return;
        }

        scrollTarget.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }

      validateRequiredOptionsAndScroll() {
        const missing = this.getMissingRequiredOptions();
        if (!missing.length) return true;

        this._requiredOptionErrorsVisible = true;
        this.updateRequiredOptionValidation(true);
        this.scrollToRequiredOption(missing[0]);
        return false;
      }

      clearManualOptionSelections() {
        if (!this.variantSelectors) return;

        this.variantSelectors.querySelectorAll('[data-required-option]').forEach((container) => {
          if (container.tagName === 'FIELDSET') {
            container.querySelectorAll('input[type="radio"]:checked').forEach((radio) => {
              radio.checked = false;
            });
            container.querySelectorAll('label.active').forEach((label) => {
              label.classList.remove('active');
            });
          } else {
            const select = container.querySelector('select.select__select');
            if (select) select.value = '';
          }
        });
      }

      hideRequiredOptionErrors() {
        if (!this.variantSelectors) return;

        this._requiredOptionErrorsVisible = false;
        this.variantSelectors.querySelectorAll('[data-variant-option-error]').forEach((errorEl) => {
          errorEl.textContent = '';
          errorEl.hidden = true;
        });
      }

      refreshRequiredOptionErrors() {
        if (this._requiredOptionErrorsVisible) {
          this.updateRequiredOptionValidation(true);
          if (!this.getMissingRequiredOptions().length) {
            this._requiredOptionErrorsVisible = false;
          }
          return;
        }

        this.hideRequiredOptionErrors();
      }

      updateRequiredOptionValidation(showMessages = false) {
        if (!this.variantSelectors) return;

        const groups = this.requiresManualVariantSelection() ? this.getRequiredOptionGroups() : { size: false, width: false };
        const messages = {
          gender: 'Please select a Gender.',
          width: 'Please select a Width.',
          size: 'Please select a Size.',
        };

        ['gender', 'width', 'size'].forEach((key) => {
          if (key === 'gender') {
            if (this.closest('quick-add-modal')) return;
            if (!this.variantSelectors.querySelector('[data-required-option="gender"]')) return;
          } else if (!this.hasRequiredOption(key)) {
            return;
          }

          const container = this.variantSelectors.querySelector(`[data-required-option="${key}"]`);
          const errorEl = container?.querySelector(`[data-variant-option-error="${key}"]`);
          const missing = key === 'gender' ? !this.isGenderSelected() : !groups[key];

          if (errorEl) {
            if (showMessages && missing) {
              errorEl.textContent = messages[key];
              errorEl.hidden = false;
            } else {
              errorEl.textContent = '';
              errorEl.hidden = true;
            }
          }

          if (container?.tagName === 'FIELDSET') {
            container.querySelectorAll('input[type="radio"]').forEach((radio) => {
              radio.setAttribute('aria-invalid', showMessages && missing ? 'true' : 'false');
            });
          } else if (key !== 'gender') {
            const select = container?.querySelector('select.select__select');
            if (select) select.setAttribute('aria-invalid', showMessages && missing ? 'true' : 'false');
          }
        });
      }

      initRequiredOptionValidation() {
        if (!this.requiresManualVariantSelection()) return;

        this._onRequiredOptionChange =
          this._onRequiredOptionChange || this.onRequiredOptionChange.bind(this);
        this.variantSelectors?.addEventListener('change', this._onRequiredOptionChange);

        // Keep any options already selected on load — only ask for what's still missing
        this.updateRequiredOptionValidation(false);

        if (!this.areRequiredOptionsSelected()) {
          this.updateVariantInputs('');
          const variantIdInput = this.getVariantIdInput();
          if (variantIdInput) {
            variantIdInput.value = '';
            variantIdInput.disabled = true;
          }

          if (this.dataset.updateUrl !== 'false') {
            const url = new URL(window.location.href);
            if (url.searchParams.has('variant')) {
              url.searchParams.delete('variant');
              window.history.replaceState({}, '', url.toString());
            }
          }
        }
      }

      onRequiredOptionChange() {
        if (!this.requiresManualVariantSelection()) return;
        this.refreshRequiredOptionErrors();
        this.updateManualVariantState();
      }

      getProductData() {
        const productId = this.dataset.productId;
        if (!productId) return null;

        const script = document.getElementById(`ProductJson-${productId}`);
        if (!script) return null;

        try {
          return JSON.parse(script.textContent);
        } catch (e) {
          return null;
        }
      }

      findMatchingVariant(product) {
        if (!product?.variants || !this.areRequiredOptionsSelected()) return null;

        const selectedByPosition = {};

        this.variantSelectors?.querySelectorAll('input[type="radio"]:checked[data-option-position]').forEach((input) => {
          selectedByPosition[input.dataset.optionPosition] = input.value;
        });

        this.variantSelectors?.querySelectorAll('select.select__select').forEach((select) => {
          const option = select.selectedOptions[0];
          if (option?.value && option.dataset.optionPosition) {
            selectedByPosition[option.dataset.optionPosition] = option.value;
          }
        });

        const selectedPositions = Object.keys(selectedByPosition);
        if (!selectedPositions.length) return null;

        return (
          product.variants.find((variant) =>
            selectedPositions.every(
              (position) => variant.options[Number(position) - 1] === selectedByPosition[position]
            )
          ) || null
        );
      }

      updateManualVariantState() {
        if (!this.requiresManualVariantSelection()) return;

        if (!this.areRequiredOptionsSelected()) {
          this.updateVariantInputs('');
          this.setAwaitingSelectionButton();
          return;
        }

        const variant = this.findMatchingVariant(this.getProductData());

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateVariantInputs(variant.id);

        if (variant.available) {
          this.toggleSubmitButton(false);
        } else {
          this.toggleSubmitButton(true, window.variantStrings.soldOut);
        }

        if (this.dataset.updateUrl !== 'false') {
          this.updateURL(this.dataset.url, variant.id);
        }
      }

      isManualRequiredInput(input) {
        const optionName = (input?.dataset?.optionName || '').toLowerCase();
        return optionName.includes('size') || optionName.includes('width');
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        if (this.requiresManualVariantSelection()) {
          this.refreshRequiredOptionErrors();
          this.updateManualVariantState();
        }

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.handleErrorMessage();
        if (this.requiresManualVariantSelection()) {
          this.setAwaitingSelectionButton();
        } else {
          this.toggleSubmitButton(true);
        }
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          const variantId = this.areRequiredOptionsSelected() ? variant?.id : null;
          this.updateURL(productUrl, variantId);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // Avoid forced focus on touch devices because it can trigger scroll/flicker.
            const isTouchDevice =
              window.matchMedia?.('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
            if (!isTouchDevice) {
              document.querySelector(`#${targetId}`)?.focus();
            }
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        if (!productInfoNode) return null;
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.textContent?.trim();
        if (!selectedVariant) return null;
        try {
          const parsed = JSON.parse(selectedVariant);
          return parsed || null;
        } catch (e) {
          return null;
        }
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          const filteredOptionValues = optionValues.filter(Boolean);
          if (filteredOptionValues.length) {
            params.push(`option_values=${filteredOptionValues.join(',')}`);
          }
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          // Preserve selected options (Size and Width only, not Color)
          const preservedSelections = this.preserveSelectedOptions();

          const currentVariantSelects = this.variantSelectors;
          if (!currentVariantSelects) return;

          const didSyncInPlace = this.syncVariantSelectsInPlace(currentVariantSelects, variantSelects);
          if (!didSyncInPlace) {
            currentVariantSelects.innerHTML = variantSelects.innerHTML;
          }
          this.restoreSelectedOptionsSilently(currentVariantSelects, preservedSelections);

          // Size labels are rebuilt from server HTML (men sizes) — re-apply offset when needed
          // (women_only tag and/or Women gender selection)
          const unisexFilter = currentVariantSelects.querySelector('unisex-size-filter');
          if (unisexFilter?.applyFilter) {
            unisexFilter.applyFilter(
              preservedSelections.gender || resolveUnisexGenderLabel(currentVariantSelects)
            );
          }
        }
      }

      syncVariantSelectsInPlace(currentVariantSelects, nextVariantSelects) {
        const currentRadios = Array.from(currentVariantSelects.querySelectorAll('input[type="radio"][data-option-value-id]'));
        const nextRadios = Array.from(nextVariantSelects.querySelectorAll('input[type="radio"][data-option-value-id]'));
        const currentSelects = Array.from(currentVariantSelects.querySelectorAll('select.select__select'));
        const nextSelects = Array.from(nextVariantSelects.querySelectorAll('select.select__select'));

        if (currentRadios.length !== nextRadios.length || currentSelects.length !== nextSelects.length) {
          return false;
        }

        currentRadios.forEach((radio, index) => {
          const nextRadio = nextRadios[index];
          if (!nextRadio) return;

          if (!this.isManualRequiredInput(radio)) {
            radio.checked = nextRadio.checked;
          }
          radio.disabled = nextRadio.disabled;
          radio.className = nextRadio.className;
          radio.dataset.productUrl = nextRadio.dataset.productUrl || '';
          radio.dataset.optionDisplayValue = nextRadio.dataset.optionDisplayValue || '';

          const currentLabel = currentVariantSelects.querySelector(`label[for="${radio.id}"]`);
          const nextLabel = nextVariantSelects.querySelector(`label[for="${nextRadio.id}"]`);
          if (currentLabel && nextLabel) {
            currentLabel.innerHTML = nextLabel.innerHTML;
          }
        });

        currentSelects.forEach((select, index) => {
          const nextSelect = nextSelects[index];
          if (!nextSelect) return;

          const currentOptions = Array.from(select.options);
          const nextOptions = Array.from(nextSelect.options);
          if (currentOptions.length !== nextOptions.length) {
            select.innerHTML = nextSelect.innerHTML;
            return;
          }

          currentOptions.forEach((option, optionIndex) => {
            const nextOption = nextOptions[optionIndex];
            if (!nextOption) return;

            const optionName = (nextOption.dataset.optionName || '').toLowerCase();
            const isManualRequired = optionName.includes('size') || optionName.includes('width');

            if (!isManualRequired) {
              option.selected = nextOption.selected;
            }
            option.disabled = nextOption.disabled;
            option.textContent = nextOption.textContent;
            option.dataset.productUrl = nextOption.dataset.productUrl || '';
            option.dataset.optionDisplayValue = nextOption.dataset.optionDisplayValue || '';
            if (!isManualRequired) {
              if (nextOption.hasAttribute('selected')) {
                option.setAttribute('selected', 'selected');
              } else {
                option.removeAttribute('selected');
              }
            }
          });
        });

        const currentSelectedVariant = currentVariantSelects.querySelector('[data-selected-variant]');
        const nextSelectedVariant = nextVariantSelects.querySelector('[data-selected-variant]');
        if (currentSelectedVariant && nextSelectedVariant) {
          currentSelectedVariant.textContent = nextSelectedVariant.textContent;
        }

        return true;
      }
      
      preserveSelectedOptions() {
        const selections = {};
        const variantSelects = this.variantSelectors;
        if (!variantSelects) return selections;
        
        // Preserve unisex gender toggle (Men/Women) so it stays when size is selected
        const activeGenderBtn =
          variantSelects.querySelector('.js-unisex-gender-toggle.g-active') ||
          variantSelects.querySelector('.js-unisex-gender-toggle[aria-pressed="true"]');
        if (activeGenderBtn) {
          selections.gender = activeGenderBtn.dataset.genderLabel || activeGenderBtn.textContent.trim() || '';
        }
        
        // Only preserve Size and Width, not Color (Color will be selected by default)
        variantSelects.querySelectorAll('input[type="radio"]:checked[data-option-name]').forEach((input) => {
          const name = (input.dataset.optionName || '').toLowerCase();
          if (name.includes('size')) selections.size = input.value;
          else if (name.includes('width')) selections.width = input.value;
        });

        variantSelects.querySelectorAll('[data-required-option]').forEach((container) => {
          const optionKey = container.dataset.requiredOption;
          if (optionKey !== 'size' && optionKey !== 'width') return;
          if (selections[optionKey]) return;

          if (container.tagName === 'FIELDSET') {
            const checked = container.querySelector('input[type="radio"]:checked');
            if (checked) selections[optionKey] = checked.value;
          } else {
            const select = container.querySelector('select.select__select');
            if (select?.value) selections[optionKey] = select.value;
          }
        });
        
        return selections;
      }
      
      restoreSelectedOptionsSilently(newVariantSelects, selections) {
        if (!newVariantSelects || !selections) return;
        
        // Restore unisex gender toggle (Men/Women) so selected tab stays
        if (selections.gender) {
          const genderButtons = newVariantSelects.querySelectorAll('.js-unisex-gender-toggle');
          genderButtons.forEach((btn) => {
            const btnLabel = btn.dataset.genderLabel || btn.textContent.trim() || '';
            const isMatch = btnLabel.toLowerCase() === selections.gender.toLowerCase();
            btn.classList.toggle('g-active', isMatch);
            btn.setAttribute('aria-pressed', isMatch ? 'true' : 'false');
          });
          const unisexFilter = newVariantSelects.querySelector('unisex-size-filter');
          if (unisexFilter && typeof unisexFilter.applyFilter === 'function') {
            unisexFilter.applyFilter(selections.gender);
          }
          // Re-apply after HTML swap so Women keeps women-only metafield colors visible
          applyWomenOnlySwatchVisibility(newVariantSelects);
        }
        
        // Restore button type options (Size/Width) - silently, no change events
        newVariantSelects.querySelectorAll('[data-required-option]').forEach((container) => {
          const optionKey = container.dataset.requiredOption;
          if (optionKey !== 'size' && optionKey !== 'width') return;

          if (container.tagName === 'FIELDSET') {
            if (selections[optionKey]) {
              const input = container.querySelector(`input[type="radio"][value="${selections[optionKey]}"]`);
              if (input && !input.disabled && !input.classList.contains('disabled')) {
                container.querySelectorAll('input[type="radio"]').forEach((radio) => {
                  radio.checked = false;
                });
                input.checked = true;
                const label = container.querySelector(`label[for="${input.id}"]`);
                if (label) {
                  container.querySelectorAll('label').forEach((l) => l.classList.remove('active'));
                  label.classList.add('active');
                }
              }
            } else {
              container.querySelectorAll('input[type="radio"]').forEach((radio) => {
                radio.checked = false;
              });
              container.querySelectorAll('label.active').forEach((label) => {
                label.classList.remove('active');
              });
            }
            return;
          }

          const select = container.querySelector('select.select__select');
          if (!select) return;

          if (selections[optionKey]) {
            const option = Array.from(select.options).find((opt) => opt.value === selections[optionKey]);
            if (option && !option.disabled) {
              select.value = selections[optionKey];
            }
          } else {
            select.value = '';
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          this.updateOptionValues(html);

          const requiredSelected = this.areRequiredOptionsSelected();
          this.refreshRequiredOptionErrors();

          if (!requiredSelected) {
            this.updateVariantInputs('');
            this.setAwaitingSelectionButton();
            const variantIdInput = this.getVariantIdInput();
            if (variantIdInput) variantIdInput.disabled = true;
            return;
          }

          const variant = this.getSelectedVariant(html) || this.findMatchingVariant(this.getProductData());

          this.pickupAvailability?.update(variant);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          const variantIdInput = this.getVariantIdInput();
          if (variantIdInput) variantIdInput.disabled = false;

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.toggleSubmitButton(
            !variant.available,
            variant.available ? undefined : window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          if (this.requiresManualVariantSelection()) {
            this.updateManualVariantState();
          }
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          if (!input) return;
          input.value = variantId ?? '';
          input.disabled = this.requiresManualVariantSelection() && !variantId;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        const params = new URLSearchParams();
        if (variantId) params.set('variant', variantId);

        const activeGenderBtn = this.querySelector('.js-unisex-gender-toggle.g-active');
        const genderParam =
          activeGenderBtn?.dataset?.genderParam ||
          (getGenderLabel(activeGenderBtn) === 'women'
            ? 'women'
            : getGenderLabel(activeGenderBtn) === 'men'
              ? 'men'
              : '');
        if (genderParam) params.set('gender', genderParam);

        const query = params.toString();
        const nextUrl = `${url}${query ? `?${query}` : ''}`;

        this.querySelector('share-button')?.updateUrl(`${window.shopUrl}${nextUrl}`);

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', nextUrl);
      }

      setUnavailable() {
        this.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallery = this.querySelector('media-gallery');
        const currentActiveMediaId = mediaGallery?.querySelector('[data-media-id].is-active')?.dataset.mediaId;
        const nextMediaId = `${this.dataset.section}-${variantFeaturedMediaId}`;
        if (currentActiveMediaId === nextMediaId) return;

        const mediaGallerySource = mediaGallery?.querySelector('ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        mediaGallery?.setActiveMedia?.(nextMediaId, true, true);

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.getVariantIdInput()?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (const selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (const attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      getVariantIdInput() {
        const productFormEl = this.productForm;
        if (productFormEl?.variantIdInput) return productFormEl.variantIdInput;
        return this.querySelector(`#product-form-${this.dataset.section} input[name="id"]`);
      }

      toggleSubmitButton(disable = true, text, soft = false) {
        const productFormEl = this.productForm;
        if (typeof productFormEl?.toggleSubmitButton === 'function') {
          productFormEl.toggleSubmitButton(disable, text, soft);
          return;
        }

        const submitButton = this.querySelector('[type="submit"].product-form__submit');
        const submitButtonText = submitButton?.querySelector('span');
        if (!submitButton) return;

        if (disable) {
          if (soft) {
            submitButton.removeAttribute('disabled');
            submitButton.classList.add('product-form__submit--awaiting-selection');
          } else {
            submitButton.setAttribute('disabled', 'disabled');
            submitButton.classList.remove('product-form__submit--awaiting-selection');
          }
          if (text && submitButtonText) submitButtonText.textContent = text;
        } else {
          submitButton.removeAttribute('disabled');
          submitButton.classList.remove('product-form__submit--awaiting-selection');
          if (submitButtonText) submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}


if (!customElements.get('unisex-size-filter')) {
  class UnisexSizeFilter extends HTMLElement {
    connectedCallback() {
      if (this.closest('quick-add-modal')) return;
      try {
        this.sectionId = this.dataset.sectionId || this.getAttribute('data-section-id');
        this.sizeOptionName =
          (this.dataset.sizeOptionName || this.getAttribute('data-size-option-name') || 'Size')
            .toString()
            .toLowerCase();
        this.womenSizeOffset = parseFloat(this.dataset.womenSizeOffset || this.getAttribute('data-women-size-offset') || '1.5');
        if (!Number.isFinite(this.womenSizeOffset)) this.womenSizeOffset = 1.5;

        const configScript = this.querySelector('.unisex-size-filter__config');
        // Config is optional now (we no longer hide sizes by gender),
        // but keep parsing for backwards compatibility / future use.
        if (configScript) {
          try {
            const rawConfig = JSON.parse(configScript.textContent || '[]');
            this.sizeMap = this.buildSizeMap(rawConfig);
          } catch (e) {
            console.error('UnisexSizeFilter: invalid config JSON', e);
            this.sizeMap = null;
          }
        } else {
          this.sizeMap = null;
        }

        // Find the nearest variant-selects container
        const productInfo = this.closest('product-info');
        this.variantSelects = productInfo?.variantSelectors || this.closest('variant-selects') || null;
        if (!this.variantSelects) return;

        // Gender toggle buttons for unisex products (optional — women_only offset still applies without them)
        this.genderButtons = Array.from(
          this.variantSelects.querySelectorAll('.js-unisex-gender-toggle')
        );

        if (this.genderButtons.length) {
          this.onGenderClick = this.onGenderClick.bind(this);
          this.genderButtons.forEach((btn) => {
            btn.addEventListener('click', this.onGenderClick);
          });

          const initialBtn = this.genderButtons.find((btn) => btn.classList.contains('g-active'));
          this.currentGender = initialBtn
            ? initialBtn.dataset.genderLabel || initialBtn.textContent.trim()
            : '';
        } else {
          this.currentGender = '';
        }

        // Size options are siblings after this element — apply after they exist in the DOM
        const runApply = () => this.applyFilter(this.currentGender || '');
        runApply();
        requestAnimationFrame(runApply);
        setTimeout(runApply, 0);
      } catch (e) {
        console.error('UnisexSizeFilter: initialization error', e);
      }
    }

    disconnectedCallback() {
      if (this.genderButtons) {
        this.genderButtons.forEach((btn) => {
          btn.removeEventListener('click', this.onGenderClick);
        });
      }
    }

    buildSizeMap(config) {
      const entries = Array.isArray(config) ? config : [config];
      const map = {};

      entries.forEach((entry) => {
        if (!entry) return;
        const rawGender = entry.gender || entry.Gender;
        const rawSizes = entry.size || entry.Size || entry.sizes || entry.Sizes;
        if (!rawGender || !rawSizes) return;

        const genderKey = String(rawGender).toLowerCase().trim();
        const sizes = String(rawSizes)
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

        if (!sizes.length) return;
        map[genderKey] = sizes;
      });

      return Object.keys(map).length ? map : null;
    }

    onGenderClick(event) {
      const btn = event.currentTarget;
      const genderLabel = btn.dataset.genderLabel || btn.textContent.trim();
      this.currentGender = genderLabel;

      // Update pressed / active state
      this.genderButtons.forEach((button) => {
        const isActive = button === btn;
        button.classList.toggle('g-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });

      this.applyFilter(genderLabel);
      syncUnisexGenderInUrl(genderLabel);

      const productInfo = this.closest('product-info');
      if (productInfo?.refreshRequiredOptionErrors) {
        productInfo.refreshRequiredOptionErrors();
      }
    }

    applyFilter(genderLabel) {
      if (!this.variantSelects) return;
      const key = String(genderLabel || '').toLowerCase().trim();
      const isWomenGender = key === 'women' || key === 'woman' || key === 'female';
      // women_only tag → always show women sizing (+1.5), even if no gender button is selected
      const isWomenOnly = this.variantSelects.hasAttribute('data-current-women-only');
      const useWomenOffset = isWomenOnly || isWomenGender;

      // Radio button (pill) size options
      const radioInputs = Array.from(
        this.variantSelects.querySelectorAll(
          'input[type="radio"][data-option-name][data-option-display-value]'
        )
      ).filter((input) => {
        const name = (input.dataset.optionName || '').toLowerCase();
        return name.includes('size');
      });

      radioInputs.forEach((input) => {
        const label = this.variantSelects.querySelector(`label[for="${input.id}"]`);
        const baseValue = (input.value || '').toString().trim();
        const displayValue = useWomenOffset
          ? this.addOffsetToSize(baseValue, this.womenSizeOffset)
          : baseValue;

        // Update only the visible size text (avoid capturing visually-hidden unavailable text)
        if (label) {
          const sizeTextEl = label.querySelector('.unisex-size-text');
          if (sizeTextEl) sizeTextEl.textContent = displayValue;
        }

        // Keep dataset aligned with what user sees (used by other logic)
        input.dataset.optionDisplayValue = displayValue;
      });

      // Dropdown size options
      const selects = Array.from(
        this.variantSelects.querySelectorAll('select.select__select')
      );

      selects.forEach((select) => {
        const labelEl = select
          .closest('.product-form__input--dropdown')
          ?.querySelector('.form__label');
        if (!labelEl) return;

        const labelText = labelEl.textContent.toLowerCase();
        if (!labelText.includes('size')) return;

        Array.from(select.options).forEach((option) => {
          const value = (option.value || '').toString().trim();
          if (!value) return;

          if (!option.dataset.unisexOriginalText) {
            option.dataset.unisexOriginalText = option.textContent;
          }
          option.textContent = useWomenOffset
            ? this.addOffsetToSize(value, this.womenSizeOffset)
            : option.dataset.unisexOriginalText;
        });
      });
    }

    addOffsetToSize(value, offset) {
      // Only transform pure numeric sizes like "5", "5.5", "10.0"
      const raw = String(value).trim();
      const num = parseFloat(raw);
      if (!Number.isFinite(num) || String(num) === 'NaN') return raw;
      const next = num + offset;
      // Display without trailing .0; keep .5 etc.
      const rounded = Math.round(next * 2) / 2;
      return Number.isInteger(rounded) ? String(rounded) : String(rounded);
    }
  }

  customElements.define('unisex-size-filter', UnisexSizeFilter);
}

function getGenderLabel(btn) {
  return (btn?.dataset.genderLabel || btn?.textContent?.trim() || '').toLowerCase();
}

function resolveUnisexGenderLabel(variantSelects) {
  if (!variantSelects) return '';
  const active =
    variantSelects.querySelector('.js-unisex-gender-toggle.g-active') ||
    variantSelects.querySelector('.js-unisex-gender-toggle[aria-pressed="true"]');
  if (active) {
    return active.dataset.genderLabel || active.textContent.trim() || '';
  }
  // No gender selected — applyFilter still adds +1.5 when data-current-women-only is present
  return '';
}

function syncUnisexGenderInUrl(genderLabel) {
  if (typeof window === 'undefined') return;
  const key = String(genderLabel || '').toLowerCase().trim();
  let genderParam = '';
  if (key === 'women' || key === 'woman' || key === 'female') genderParam = 'women';
  else if (key === 'men' || key === 'man' || key === 'male') genderParam = 'men';
  if (!genderParam) return;

  const url = new URL(window.location.href);
  url.searchParams.set('gender', genderParam);
  window.history.replaceState({}, '', url.toString());
}

function normalizeProductPath(url) {
  try {
    return new URL(url, window.location.origin).pathname.replace(/\/$/, '');
  } catch (e) {
    return '';
  }
}

function syncActiveColorSwatchFromUrl(scope) {
  const productInfo = scope?.closest?.('product-info') || scope;
  const variantSelects =
    productInfo?.querySelector?.('variant-selects') ||
    (scope?.matches?.('variant-selects') ? scope : scope?.querySelector?.('variant-selects'));
  if (!variantSelects) return null;

  const swatches = Array.from(variantSelects.querySelectorAll('a.swatch-input__label[href]'));
  if (!swatches.length) return null;

  const currentPath = normalizeProductPath(window.location.href);
  const productInfoUrl = productInfo?.dataset?.url ? normalizeProductPath(productInfo.dataset.url) : '';

  const matchingSwatch = swatches.find((swatch) => {
    const swatchPath = normalizeProductPath(swatch.href);
    return swatchPath && (swatchPath === currentPath || (productInfoUrl && swatchPath === productInfoUrl));
  });

  if (!matchingSwatch) return null;

  swatches.forEach((swatch) => {
    swatch.classList.toggle('is-active', swatch === matchingSwatch);
  });

  return matchingSwatch;
}

function updateMenColorSwatchSelection(variantSelects) {
  if (!variantSelects) return null;

  const synced = syncActiveColorSwatchFromUrl(variantSelects);
  if (synced && !synced.hasAttribute('data-is-women-only')) {
    return synced;
  }

  const swatches = Array.from(variantSelects.querySelectorAll('.swatch-input__label'));
  const firstMenSwatch = swatches.find((swatch) => !swatch.hasAttribute('data-is-women-only'));

  if (!firstMenSwatch) return null;

  swatches.forEach((swatch) => {
    swatch.classList.toggle('is-active', swatch === firstMenSwatch);
  });

  return firstMenSwatch;
}

function finalizeMenGenderSwitch(productInfo) {
  if (!productInfo) return;

  const variantSelects = productInfo.querySelector('variant-selects');
  if (!variantSelects) return;

  variantSelects.querySelectorAll('.js-unisex-gender-toggle').forEach((btn) => {
    const isMen = getGenderLabel(btn) === 'men';
    btn.classList.toggle('g-active', isMen);
    btn.setAttribute('aria-pressed', isMen ? 'true' : 'false');
  });

  variantSelects.removeAttribute('data-current-women-only');
  updateMenColorSwatchSelection(variantSelects);
  applyWomenOnlySwatchVisibility(productInfo);

  const unisexFilter = variantSelects.querySelector('unisex-size-filter');
  if (unisexFilter?.applyFilter) {
    unisexFilter.applyFilter('Men');
  }
  syncUnisexGenderInUrl('Men');
}

function isOnWomenOnlyProduct(variantSelects) {
  if (!variantSelects) return false;
  if (variantSelects.hasAttribute('data-current-women-only')) return true;
  const activeSwatch = variantSelects.querySelector('.swatch-input__label.is-active');
  return activeSwatch?.hasAttribute('data-is-women-only');
}

function navigateToFirstMenColor(productInfo, variantSelects) {
  if (!productInfo || !variantSelects) return;

  const firstMenSwatch = updateMenColorSwatchSelection(variantSelects);
  if (!firstMenSwatch?.href) return;

  const url = new URL(firstMenSwatch.href, window.location.origin);
  const cleanProductUrl = url.pathname;
  const params = new URLSearchParams(url.search);
  params.set('section_id', productInfo.sectionId);
  const requestUrl = `${cleanProductUrl}?${params.toString()}`;
  const sectionId = productInfo.dataset.section;

  productInfo.renderProductInfo({
    requestUrl,
    targetId: 'gender-switch',
    callback: (html) => {
      const swapFn = productInfo.handleSwapProduct(cleanProductUrl, false);
      swapFn(html);

      requestAnimationFrame(() => {
        const newProductInfo = document.getElementById(`MainProduct-${sectionId}`);
        finalizeMenGenderSwitch(newProductInfo);
      });
    },
  });
}

function applyWomenOnlySwatchVisibility(scope) {
  const productInfos = scope
    ? [scope.closest?.('product-info') || scope].filter(Boolean)
    : Array.from(document.querySelectorAll('product-info'));

  productInfos.forEach((productInfo) => {
    const variantSelects = productInfo.querySelector('variant-selects');
    if (!variantSelects) return;

    const womenOnlySwatches = variantSelects.querySelectorAll('[data-is-women-only="true"]');
    if (!womenOnlySwatches.length) return;

    // Women-only template: always show all colors (including women-only)
    if (variantSelects.hasAttribute('data-women-only-template')) {
      womenOnlySwatches.forEach((swatch) => {
        swatch.style.display = '';
      });
      syncActiveColorSwatchFromUrl(variantSelects);
      return;
    }

    // Default template: women-only products are omitted in Liquid; keep toggle as safety net
    const activeGenderBtn =
      variantSelects.querySelector('.js-unisex-gender-toggle.g-active') ||
      variantSelects.querySelector('.gender_button_row .g-active');

    const gender = getGenderLabel(activeGenderBtn) || 'men';

    womenOnlySwatches.forEach((swatch) => {
      swatch.style.display = gender === 'women' ? 'block' : 'none';
    });

    if (gender === 'men' || gender === 'man') {
      const activeSwatch = variantSelects.querySelector('.swatch-input__label.is-active');
      const needsMenSelection =
        !activeSwatch ||
        activeSwatch.hasAttribute('data-is-women-only') ||
        window.getComputedStyle(activeSwatch).display === 'none';

      if (needsMenSelection) {
        updateMenColorSwatchSelection(variantSelects);
      } else {
        syncActiveColorSwatchFromUrl(variantSelects);
      }
    } else {
      syncActiveColorSwatchFromUrl(variantSelects);
    }
  });
}

document.addEventListener('DOMContentLoaded', () => {
  applyWomenOnlySwatchVisibility();
  document.querySelectorAll('product-info').forEach((el) => syncActiveColorSwatchFromUrl(el));
});
document.addEventListener('product-info:loaded', (event) => {
  applyWomenOnlySwatchVisibility(event.target);
  syncActiveColorSwatchFromUrl(event.target);
});

document.addEventListener('click', (event) => {
  const btn = event.target.closest('.variant-button');
  if (!btn) return; // only act when a gender button is clicked

  const productUrl = btn.getAttribute('product-url');
  const productInfo = btn.closest('product-info');
  if (!productUrl || !productInfo) return;
  if (productInfo.closest('quick-add-modal')) return;

  // optional: show loading state
  btn.classList.add('loading');

  productInfo.renderProductInfo({
    requestUrl: productUrl,
    targetId: btn.id || 'gender-switch',
    callback: productInfo.handleSwapProduct(productUrl, false), // only swap inside product-info
  });

  // optional: remove loading after short delay
  setTimeout(() => btn.classList.remove('loading'), 1500);
});

// Same-page gender selection (current product — no redirect)
document.addEventListener('click', (event) => {
  const genderBtn = event.target.closest('.js-gender-param-toggle');
  if (!genderBtn) return;

  const container = genderBtn.closest('.gender_button_row');
  container?.querySelectorAll('.variant-button[data-gender-param]').forEach((btn) => {
    btn.classList.toggle('g-active', btn === genderBtn);
  });

  const productInfo = genderBtn.closest('product-info');
  if (productInfo?.refreshRequiredOptionErrors) {
    productInfo.refreshRequiredOptionErrors();
  }
  if (productInfo?.updateManualVariantState) {
    productInfo.updateManualVariantState();
  }
});

// Ensure unisex gender buttons always reflect the current selection
document.addEventListener('click', (event) => {
  const genderBtn = event.target.closest('.js-unisex-gender-toggle');
  if (!genderBtn) return;

  const container = genderBtn.closest('.gender_button_row') || document;
  const buttons = container.querySelectorAll('.js-unisex-gender-toggle');
  const switchingToMen = getGenderLabel(genderBtn) === 'men';
  const genderLabel = genderBtn.dataset.genderLabel || genderBtn.textContent.trim();

  buttons.forEach((btn) => {
    const isActive = btn === genderBtn;
    btn.classList.toggle('g-active', isActive);
    btn.setAttribute('aria-pressed', isActive ? 'true' : 'false');
  });

  // Re-apply size offset after any DOM swap that may have reset labels
  const variantSelects = genderBtn.closest('variant-selects');
  const unisexFilter = variantSelects?.querySelector('unisex-size-filter');
  if (unisexFilter?.applyFilter) {
    unisexFilter.applyFilter(genderLabel);
  }
  syncUnisexGenderInUrl(genderLabel);

  applyWomenOnlySwatchVisibility(genderBtn);

  if (switchingToMen) {
    const productInfo = genderBtn.closest('product-info');
    if (isOnWomenOnlyProduct(variantSelects)) {
      navigateToFirstMenColor(productInfo, variantSelects);
    } else {
      syncActiveColorSwatchFromUrl(variantSelects) || updateMenColorSwatchSelection(variantSelects);
    }
  } else {
    syncActiveColorSwatchFromUrl(variantSelects || genderBtn);
  }
  const productInfo = genderBtn.closest('product-info');
  if (productInfo?.refreshRequiredOptionErrors) {
    productInfo.refreshRequiredOptionErrors();
  }
  if (productInfo?.updateManualVariantState) {
    productInfo.updateManualVariantState();
  }

  // If the size guide popup is already open, update its visible table
  if (window.PahiModal && typeof window.PahiModal.open === 'function') {
    const modalEl = document.querySelector('.pahi-modal');
    if (modalEl && modalEl.classList.contains('pahi-modal--active')) {
      window.PahiModal.open(genderLabel);
    }
  }
});

// Unisex size guide: when clicking \"Size guide\" on a unisex product,
// show Men/Women table based on selected gender (data-size-guide-table)
document.addEventListener('click', (event) => {
  const sizeGuideBtn = event.target.closest('#size_guide-popup');
  if (!sizeGuideBtn) return;

  // Only override behavior when unisex gender toggles exist on this product
  const root = sizeGuideBtn.closest('product-info') || document;
  const activeGenderBtn = root.querySelector('.js-unisex-gender-toggle.g-active');
  if (!activeGenderBtn) return; // non‑unisex products keep default metafield behavior

  const gender =
    activeGenderBtn.dataset.genderLabel || activeGenderBtn.textContent.trim() || 'Men';

  if (window.PahiModal && typeof window.PahiModal.open === 'function') {
    // Inline onclick=\"PahiModal.open()\" may still fire first and show \"all\",
    // this call will immediately switch the visible table to men/women.
    window.PahiModal.open(gender);
  }
});
